import type { Error as Auth0Error } from "@auth0/auth0-acul-js";
import type { MfaEnrollFactorType } from "@auth0/auth0-acul-js/mfa-begin-enroll-options";

import { butterfieldLogo } from "@/assets/logos";
import { useMfaEnrollmentManager } from "../hooks/useMfaEnrollmentManager";

// const heroImageUrl =
//   "https://media.butterfield.com/page-image_imageUrl_a470c5db-2106-4964-a8f2-414c0415efe6_1744290400564.jpg";
const heroImageUrl =
  "https://cdn.mediavalet.com/cato/butterfield/aEeF6_P6lk6o6VqdMg_3cQ/GX_bog2pTkawcjy_quukQA/Large/DSC09355-slovenia-ivan-sardi-2026.jpeg?f=auto";

const FACTOR_LABELS: Record<MfaEnrollFactorType, string> = {
  otp: "Authenticator App",
  sms: "SMS Text Message",
  phone: "Phone Call",
  voice: "Voice Call",
  "push-notification": "Push Notification",
  "webauthn-roaming": "Security Key",
};

const FACTOR_DESCRIPTIONS: Record<MfaEnrollFactorType, string> = {
  otp: "Use an app like Google Authenticator to generate codes",
  sms: "Receive a one-time code via text message",
  phone: "Receive a one-time code via phone call",
  voice: "Receive a spoken code via phone call",
  "push-notification": "Approve login requests from your mobile device",
  "webauthn-roaming": "Use a hardware security key or passkey",
};

function MfaEnrollmentScreen() {
  const { handleEnroll, errors, texts, enabledFactors } =
    useMfaEnrollmentManager();

  const sdkErrors = (errors || []) as Auth0Error[];
  const generalErrors = sdkErrors.filter((err) => !err.field);

  const headingText = texts?.title || "Secure Your Account";
  const descriptionText =
    texts?.description ||
    "Add an extra layer of security by setting up two-factor authentication.";

  const factorsToShow =
    enabledFactors.length > 0
      ? enabledFactors
      : (["otp", "sms"] as MfaEnrollFactorType[]);

  return (
    <div className="overflow-hidden rounded-none bg-white shadow-[0_30px_80px_rgba(2,6,23,0.35)] min-h-screen">
      <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
        <div className="relative order-1 h-56 sm:h-72 md:order-2 md:block md:h-full">
          <img
            src={heroImageUrl}
            alt="Auth0 experience preview"
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900/10 via-transparent to-slate-900/30" />
        </div>

        <div className="flex h-full flex-col justify-center gap-6 order-2 bg-white px-10 py-12 md:order-1 md:px-12">
          <div className="mb-0 flex flex-col items-start text-left">
            <img
              src={butterfieldLogo}
              alt="Butterfield logo"
              className="h-14 w-auto"
              loading="lazy"
            />
            <p className="mt-4 font-display text-xl font-normal text-ink">
              {headingText}
            </p>
            <p className="mt-2 text-sm text-ink-2">{descriptionText}</p>
          </div>

          {generalErrors.length > 0 && (
            <div className="rounded-none border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
              <ul className="list-disc space-y-1 pl-5">
                {generalErrors.map((error, index) => (
                  <li key={`mfa-enroll-error-${index}`}>{error.message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {factorsToShow.map((factor) => (
              <button
                key={factor}
                type="button"
                onClick={() => handleEnroll(factor)}
                className="flex w-full flex-col items-start rounded-sm border border-border bg-white px-4 py-4 text-left transition hover:cursor-pointer hover:bg-btn-hover hover:shadow-sm"
              >
                <span className="text-sm font-semibold text-ink">
                  {FACTOR_LABELS[factor] || factor}
                </span>
                <span className="mt-1 text-xs text-ink-3">
                  {FACTOR_DESCRIPTIONS[factor]}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MfaEnrollmentScreen;
